/**
 * @file main.tsx
 * @description Application entry point.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider as NewToastProvider } from './components/ui/Toast';
import { ToastProvider as LegacyToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import App from './App';
import PushPermissionPrompt from './components/notifications/PushPermissionPrompt';
import * as Sentry from '@sentry/react';
import './i18n'; // I18N-T1: initialize i18next (side-effect) before any component renders
import './utils/document-beacon'; // DOC-AUDIT-1 S2: installs the browser-export beacon sender (side-effect)
import { I18nProvider } from './i18n/I18nProvider';
// ONBOARD-FIX-1 A2.2: side-effect import — starts listening for `beforeinstallprompt`
// immediately. Chromium fires it once and does not replay it, so a listener registered
// later (inside a component effect) can miss it entirely and lose one-tap install.
import './utils/pwa';
// PWA-UPDATE-1: both are called EXPLICITLY below, never as import side-effects. package.json has no
// "sideEffects" field today; if someone adds "sideEffects": false as a routine bundle-size
// optimisation, a side-effect-only import would be tree-shaken away and every gate would still pass
// green while updates silently stopped reaching installed apps.
import { initSwUpdate } from './pwa/swUpdate';
import { initChunkReloadNet } from './pwa/chunkReload';
import UpdateReadyChip from './components/pwa/UpdateReadyChip';

// Initialize Sentry browser SDK. No-op when VITE_SENTRY_DSN is unset
// (so 'npm run dev' is clean; only production builds report errors).
// sendDefaultPii: false prevents user IPs, headers, and form data from leaking.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  });
}

/* CSS imports — order matters:
 * 1. index.css: Tailwind directives + Shadcn theme + brand-mapped oklch variables
 * 2. tokens.css: Brand CSS variables (--p, --bg, --t1, etc.) used by inline styles
 * 3. pwa-shell.css: Safe-area insets, overscroll containment, touch optimizations
 */
import './index.css';
import './styles/tokens.css';
import './styles/pwa-shell.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 2, refetchOnWindowFocus: true },
    mutations: { retry: 0 },
  },
});

// SW-DEV-2: actively evict any previously-installed service worker + caches in dev.
// The ONB-1c guard stops NEW registrations, but a SW installed by an earlier prod-like
// build still controls localhost and serves stale bundles (old login UI, logout-on-nav).
// Dev-only, feature-checked, try/catch — never runs in prod (guard prevents SW there too).
if (!import.meta.env.PROD) {
  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((rs) => rs.forEach((r) => r.unregister()))
        .catch(() => {});
    }
    if ('caches' in window) {
      caches
        .keys()
        .then((ks) => ks.forEach((k) => caches.delete(k)))
        .catch(() => {});
    }
  } catch {
    /* no-op: eviction is best-effort dev cleanup */
  }
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

// PWA-UPDATE-1. PROD-only: devOptions.enabled is false so there is no service worker under
// `npm run dev`, and a chunk-reload net would fight Vite's HMR.
//
// isMutating is INJECTED rather than imported by swUpdate.ts. Two QueryClient instances exist —
// the live one created above, and an exported-but-unused one in providers/QueryProvider.tsx. A
// module importing "the" client has a 50% chance of holding the dead one, and the symptom would be
// silent: isMutating() returns 0 forever and the guard that protects unsaved work never fires.
if (import.meta.env.PROD) {
  initChunkReloadNet();
  initSwUpdate({ isMutating: () => queryClient.isMutating() });
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <NewToastProvider>
              <LegacyToastProvider>
                <I18nProvider>
                  <App />
                  {/* PWA-UPDATE-1: registration itself still belongs to vite-plugin-pwa's injected
                      registerSW.js — deliberately, since it runs outside the hashed bundle and so
                      survives a build that throws at module scope. The update DRIVER is
                      pwa/swUpdate.ts, initialised above; this chip is its only user-visible surface
                      and renders nothing unless a reload had to be deferred. */}
                  <PushPermissionPrompt />
                  <UpdateReadyChip />
                </I18nProvider>
              </LegacyToastProvider>
            </NewToastProvider>
          </AuthProvider>
        </BrowserRouter>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);